import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMe } from "@/lib/auth";
import { AccessDenied } from "@/components/access-denied";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function ReportsRouteGate({ children }: { children: ReactNode }) {
  const { me, hasSection, isLoading, error } = useMe();
  const queryClient = useQueryClient();

  if (!me && isLoading) {
    return (
      <div
        className="mx-auto flex min-h-48 max-w-2xl items-center justify-center p-6 text-sm text-muted-foreground"
        role="status"
        data-testid="reports-access-loading"
      >
        Checking Reports access…
      </div>
    );
  }

  if (!me && error) {
    return (
      <Card className="mx-auto mt-8 max-w-2xl" role="alert">
        <CardHeader>
          <CardTitle>Unable to verify Reports access</CardTitle>
          <CardDescription>
            Your access could not be checked. Retry the check or reload the page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            data-testid="reports-access-retry"
            onClick={() => void queryClient.refetchQueries({ queryKey: ["/api/me"], type: "active" })}
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!hasSection("reports")) {
    return <AccessDenied section="reports" />;
  }

  return children;
}
